import { defineComponent } from '~/utils/alpine_types'

type EventType = 'driving' | 'theory' | 'exam' | 'other'

type CalendarEvent = {
  id: number | null
  driver_id: number | null
  driving_license_class_id: number | null
  event_type: EventType
  lesson_type_id: number | null
  start_time: string | null
  end_time: string | null
  lesson_type_subject_ids: number[]
}

type LessonType = {
  id: number
  name: string
  category: string
  default_required_minutes: number | null
  driving_license_class_ids: number[]
  lesson_type_subjects: LessonTypeSubject[]
}

type LessonTypeSubject = {
  id: number
  name: string
}

type Teacher = {
  id: number
  name: string
  hidden_days: number[]
  name_with_school: string
}

type Args = {
  calendarEvent: CalendarEvent
  lessonTypes: LessonType[]
  teachers: Teacher[]
  defaultLessonMinutes: Record<EventType, number>
}

const removeSeconds = (time: string) => time.split(':', 2).join(':')

export const calendarEvent = defineComponent((args: Args) => ({
  calendarEvent: args.calendarEvent,
  lessonTypes: args.lessonTypes,
  teachers: args.teachers,
  defaultLessonMinutes: args.defaultLessonMinutes,
  recurring: false,
  lessonCountInputValue: undefined as unknown as number,

  get tab(): EventType {
    return this.calendarEvent.event_type
  },

  get availableLessonTypes(): LessonType[] {
    const licenseId = this.calendarEvent.driving_license_class_id
    if (!licenseId) return []

    return this.lessonTypes.filter(
      (lessonType) =>
        lessonType.driving_license_class_ids.includes(licenseId) &&
        lessonType.category === this.calendarEvent.event_type,
    )
  },

  get availableLessonTypeSubjects(): LessonTypeSubject[] {
    return this.selectedLessonType?.lesson_type_subjects || []
  },

  get lessonCount(): number {
    const { start_time, end_time } = this.calendarEvent
    if (!start_time || !end_time || start_time >= end_time) return 0

    const diffMs = new Date(end_time).getTime() - new Date(start_time).getTime()
    return Math.round((diffMs / 1000 / 60 / this.lessonMinutes) * 10) / 10
  },

  get lessonIncrement(): 0.5 | 1 {
    return (this.lessonMinutes / 2) % 1 === 0 ? 0.5 : 1
  },

  get lessonMinutes(): number {
    const defaultMinutes = this.defaultLessonMinutes[this.calendarEvent.event_type]

    // Exams can be either 30 min or 60 min depending on whether it's a driving exam or a theory exam
    return this.calendarEvent.event_type !== 'exam'
      ? defaultMinutes
      : this.selectedLessonType?.default_required_minutes || defaultMinutes
  },

  get lessonTypeRequired(): boolean {
    return ['driving', 'exam'].includes(this.calendarEvent.event_type)
  },

  get studentsRequired(): boolean {
    return ['driving', 'exam'].includes(this.calendarEvent.event_type)
  },

  get selectedLessonType(): LessonType | undefined {
    return this.availableLessonTypes.find(({ id }) => id === this.calendarEvent.lesson_type_id)
  },

  init() {
    // Convert timestamps to a format that the datepicker understands
    this.calendarEvent.end_time &&= removeSeconds(this.calendarEvent.end_time)
    this.calendarEvent.start_time &&= removeSeconds(this.calendarEvent.start_time)
    this.lessonCountInputValue = this.lessonCount

    // Select first available lesson type when its dependencies change
    this.selectFirstLessonType()
    this.$watch('calendarEvent.driving_license_class_id', () => this.selectFirstLessonType())
    this.$watch('calendarEvent.event_type', () => this.selectFirstLessonType())

    // Update end_time automatically when changing event type or start time
    this.$watch('calendarEvent.event_type', () => this.setEndTime())
    this.$watch('calendarEvent.lesson_type_id', () => this.calendarEvent.event_type === 'exam' && this.setEndTime())
    this.$watch('calendarEvent.start_time', (newValue: string, oldValue: string) => {
      const diff = this.calendarEvent.end_time
        ? new Date(this.calendarEvent.end_time).getTime() - new Date(oldValue).getTime()
        : null
      this.setEndTime(newValue, diff)
    })
    this.$watch('calendarEvent.end_time', () => (this.lessonCountInputValue = this.lessonCount))
  },

  selectFirstLessonType(): void {
    if (!this.availableLessonTypes.some(({ id }) => id === this.calendarEvent.lesson_type_id)) {
      this.calendarEvent.lesson_type_id = this.availableLessonTypes[0]?.id || null
    }
  },

  setEndTime(startTime: string | null = null, diffMs: number | null = null): void {
    startTime ||= this.calendarEvent.start_time
    if (!startTime) return

    diffMs ||= this.lessonMinutes * 60 * 1000
    const newEndTime = new Date(new Date(startTime).getTime() + diffMs)
    this.calendarEvent.end_time = removeSeconds(newEndTime.toLocaleString('sv').split(' ').join('T'))
  },

  changeLessonCount(): void {
    const value = +(this.$el as HTMLInputElement).value
    if (!value) return

    this.setEndTime(this.calendarEvent.start_time, 60 * 1000 * value * this.lessonMinutes)
  },

  decrementEndTime(): void {
    this.setEndTime(this.calendarEvent.end_time, this.roundedIncrementMs('down'))
  },

  incrementEndTime(): void {
    this.setEndTime(this.calendarEvent.end_time, this.roundedIncrementMs('up'))
  },

  roundedIncrementMs(direction: 'up' | 'down'): number {
    const incrementMs = this.lessonMinutes * this.lessonIncrement * 60 * 1000 * (direction === 'up' ? 1 : -1)
    const { start_time, end_time } = this.calendarEvent
    if (!end_time) return 0
    if (!start_time) return incrementMs

    const endTimeMs = new Date(end_time).getTime()
    const newEndTime = endTimeMs + incrementMs
    const diff = new Date(newEndTime).getTime() - new Date(start_time).getTime()
    const remainderMs = diff % incrementMs
    if (remainderMs === 0) return incrementMs

    // Round to the closest increment in the same direction if newEndTime isn't divisible by the lesson increment
    const roundedDownMs = incrementMs - remainderMs
    return direction === 'up' ? roundedDownMs : roundedDownMs - incrementMs
  },

  // A teacher should be disabled when their hidden_days includes the weekday of either the start or end time.
  // Doesn't take into account the days in between for multi-day events. This is because most teachers probably have
  // weekends hidden from their calendars, which would make them unavailable for multi-day events that span weekends.
  // This is deliberately not checked for "other" events currently.
  teacherUnavailable(teacherId: number): boolean {
    const teacher = this.teachers.find(({ id }) => id === teacherId)
    if (!teacher) throw new Error('Invalid teacher id')

    return [this.calendarEvent.start_time, this.calendarEvent.end_time].some((time) =>
      time ? teacher.hidden_days.includes(new Date(time).getDay()) : false,
    )
  },
}))
